import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Notification from '@/shared/components/Notification';
import { useNavigate, useParams } from "react-router-dom";
import { NoPaddingCardWrapper } from '@/shared/components/CardWrapper'
import Loader from '@/shared/components/Loader'
import { selectSelectedReport, setSelectedReport } from '@/store/slices/ReportsSlice'
import { useCurrentTool } from "@/shared/hooks/useCurrentTool"
import { useTranslation } from "react-i18next";
import ReportDetailHeader from '@/features/report-detail/ReportDetailHeader';
import ReportDetailActions from '@/features/report-detail/ReportDetailActions';
import ReportDetailAudioPlayer from '@/features/report-detail/ReportDetailAudioPlayer';
import ReportDetailFields from '@/features/report-detail/ReportDetailFields';
import ReportDetailContent from '@/features/report-detail/ReportDetailContent';
import ReportDetailEditForm from '@/features/report-detail/ReportDetailEditForm';
import { reportsApi, useGetReportQuery, useUpdateReportStatusMutation } from '@/api/rtk/reportsApi'
import { patchReportsListCaches } from '@/api/rtk/patchReportsListCaches'
import { toast } from 'react-toastify';

function Report({ isDrawer = false }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const { t } = useTranslation();
  const { currentToolFields, toolName } = useCurrentTool();
  const [updateReportStatus] = useUpdateReportStatusMutation();

  const [showNotification, setShowNotification] = useState(false);
  const [reportStatus, setReportStatus] = useState(null);
  const sliceReport = useSelector(selectSelectedReport);
  const { id } = params;
  const userToken = localStorage.getItem("user_id");

  const queryArgs = useMemo(() => {
    if (isDrawer || !userToken || !id) return null;
    return { id, currentToolFields };
  }, [isDrawer, userToken, id, currentToolFields]);

  const { data, isError } = useGetReportQuery(queryArgs, {
    skip: !queryArgs,
    refetchOnMountOrArgChange: 30,
  });

  const fetchedReport = data?.message?.[0];
  const seededReport = sliceReport?.id && sliceReport.id === id ? sliceReport : null;
  const report = isDrawer
    ? (sliceReport || {})
    : (fetchedReport ?? seededReport ?? {});
  const fileUpdating = !report?.report_fetched;

  useEffect(() => {
    if (!userToken) {
      navigate("/");
    }
  }, [userToken, navigate]);

  useEffect(() => {
    if (isDrawer) return;
    window.scrollTo(0, 0);
  }, [id, isDrawer]);

  const [editClicked, setEditClicked] = useState(false);
  const [staticFields, setStaticFields] = useState({
    comment: '',
    report: '',
  });
  const [dynamicFields, setDynamicFields] = useState(() => 
    currentToolFields.reduce((acc, field) => {
      acc[field.name] = '';
      return acc;
    }, {})
  );

  const handleInputChange = ({ key, value }) => {
    if (currentToolFields.some(field => field.name === key)) {
      setDynamicFields(prevState => ({
        ...prevState,
        [key]: value
      }));
    } else {
      setStaticFields(prevState => ({
        ...prevState,
        [key]: value
      }));
    }
  };
  
  const allowedStatus = [
    {
      name: t('reports.tabs.generated'),
      active: false,
      value: "generated",
    },
    {
      name: t('reports.tabs.inProgress'),
      active: false,
      value: "in-progress",
    },
    {
      name: t('reports.tabs.completed'),
      active: false,
      value: "completed",
    },
    {
      name: t('reports.tabs.urgent'),
      active: false,
      value: "urgent",
    },
  ];

  const handleCopyClicked = () => {
    setShowNotification(true);
    setTimeout(() => {
      setShowNotification(false);
    }, 2000);
  };

  const applyLocalReportPatch = (patch) => {
    if (isDrawer) {
      dispatch(setSelectedReport({ ...report, ...patch }));
      return;
    }
    if (!queryArgs) return;
    dispatch(reportsApi.util.updateQueryData('getReport', queryArgs, (draft) => {
      const cached = draft?.message?.[0];
      if (cached) Object.assign(cached, patch);
    }));
  };

  const handleStatusUpdate = async (status) => {
    setReportStatus(status);
    try {
      const result = await updateReportStatus({ id: report?.id, status }).unwrap();
      if (result.statusCode === 200) {
        const nextStatus = result?.status || status;
        applyLocalReportPatch({ status: nextStatus });
        dispatch(patchReportsListCaches({ ...report, status: nextStatus }, {
          toolName,
          currentToolFields,
        }));
      }
    } catch {
      toast.error('Failed to update status. Please try again.');
    }
  };

  const copyText = useMemo(() => report?.report?.split(" \\\n")?.join("\n")?.replace(/([\\#*])+\s*/g, ''), [report]);

  if (!isDrawer && isError && !report.report) {
    return (
      <NoPaddingCardWrapper className="px-6 mb-5">
        <p className="py-10 text-center text-[#71717A]">Report not found</p>
      </NoPaddingCardWrapper>
    );
  }

  return (
    <NoPaddingCardWrapper isDrawer={isDrawer} className={`flex flex-col gap-5 overflow-y-auto overflow-x-hidden no-scrollbar ${isDrawer ? '' : 'px-6 mb-5'}`}>
      <div className={`flex md:gap-2 gap-2.5 justify-between py-6 overflow-x-hidden ${editClicked ? '-mt-2' : ''} ${isDrawer ? "px-0 md:flex-row flex-col" : "flex-col"}`}>
        <ReportDetailHeader
          report={report}
          isDrawer={isDrawer}
        />
        
        <div className={`flex-col w-full flex gap-6`}>
          {!editClicked && (
            <>
              <ReportDetailActions
                report={report}
                copyText={copyText}
                allowedStatus={allowedStatus}
                reportStatus={reportStatus}
                fileUpdating={fileUpdating}
                handleStatusUpdate={handleStatusUpdate}
                handleCopyClicked={handleCopyClicked}
                setEditClicked={setEditClicked}
              />
              
              <ReportDetailAudioPlayer reportId={report?.id} audioDuration={report?.audio_duration} />
            </>
          )}
          
          <ReportDetailFields 
            report={report} 
            editClicked={editClicked} 
            dynamicFields={dynamicFields} 
            handleInputChange={handleInputChange}
          />
        </div>
      </div>
      
      {!editClicked && <div className={`${isDrawer && '-mx-6'} -mt-6 border-t border-t-[#E5E7EC]`} />}
      
      {!report.report ? (
        <div className="p-[20px] sm:p-[24px]">
          <Loader className="w-full flex justify-center" />
        </div>
      ) : (
        <>
          {editClicked ? (
            <ReportDetailEditForm 
              report={report}
              queryArgs={queryArgs}
              isDrawer={isDrawer}
              setEditClicked={setEditClicked} 
              dynamicFields={dynamicFields} 
              setDynamicFields={setDynamicFields} 
              staticFields={staticFields}
              setStaticFields={setStaticFields}
              handleInputChange={handleInputChange}
            />
          ) : (
            <ReportDetailContent
              report={report}
              isDrawer={isDrawer}
            />
          )}
        </>
      )}
      
      {showNotification && (
        <Notification text={"Copied"} color={"bg-[#1F232E]"} />
      )}
    </NoPaddingCardWrapper>
  );
}

export default Report;
